(() => {
  const t = (i18n, key, fallback) =>
    (i18n && i18n[key] != null && String(i18n[key]).trim() !== '' && !String(i18n[key]).startsWith('translation missing'))
      ? i18n[key]
      : fallback;

  const cartAddUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}cart/add.js`;
  };

  const norm = (s) =>
    String(s || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/×/g, 'x')
      .replace(/\s+/g, ' ')
      .trim();

  const includesAny = (hay, needles) => {
    if (!needles || !needles.length) return false;
    const h = norm(hay);
    return needles.some((n) => h.includes(norm(n)));
  };

  const excludesAny = (hay, needles) => {
    if (!needles || !needles.length) return true;
    const h = norm(hay);
    return !needles.some((n) => h.includes(norm(n)));
  };

  const formatMoney = (cents, _moneyFormat) => {
    if (cents == null || cents === '') return '—';
    const n = Number(cents);
    if (Number.isNaN(n)) return '—';
    const whole = Math.round(n / 100);
    return `${whole.toLocaleString('tr-TR')} ₺`;
  };

  const moneyOf = (variant, moneyFormat) => {
    if (!variant) return '—';
    return formatMoney(variant.price, moneyFormat);
  };

  /** Shopify variant price (Harfmix app already converted USD → TRY). */
  const tableboxBaseCents = (_config, _opt, variant) => Number(variant?.price) || 0;

  const tableboxPrintCents = (_config, _opt, variant) => Number(variant?.price) || 0;

  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

  const notifyHorizonCart = async (addResponse, lines) => {
    let cartJson = null;
    try {
      cartJson = await fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());
    } catch (_) {}

    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines || [],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: cartJson ? CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson) : undefined,
        detail: {
          items: cartJson?.items || [],
          source: 'tablebox-craft',
          itemCount: cartJson?.item_count,
          sections: addResponse?.sections || {},
          didError: false,
        },
      });
    } catch (err) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cartJson || addResponse, bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartJson || addResponse, bubbles: true }));
    }
  };

  class TableboxCraft extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const cfgEl = document.getElementById(this.dataset.configId || '');
      if (!cfgEl) return;
      try {
        this.config = JSON.parse(cfgEl.textContent);
      } catch (e) {
        console.error('[TableboxCraft] bad config', e);
        return;
      }

      this.i18n = this.config.i18n || {};
      this.variants = this.config.variants || [];
      this.options = (this.config.options || []).filter((opt) => {
        const base = this.variants.find(
          (v) => includesAny(v.title, opt.baseContains) && excludesAny(v.title, opt.baseExcludes || [])
        );
        return Boolean(base);
      });
      if (!this.options.length) this.options = this.config.options || [];
      this.moneyFormat = this.config.moneyFormat || '{{amount}} ₺';
      this.optionId = this.options[0]?.id || null;
      this.wantPrint = false;
      this.fileFront = null;
      this.fileBack = null;
      this._frontUrl = null;
      this._backUrl = null;

      this._cacheDom();
      this._initPrintSource();
      this._renderSizePills();
      this._bind();
      this._placePrintAfterCtas();
      this._markHosts();
      this._interceptCart();
      this._sync();
    }

    disconnectedCallback() {
      if (this._frontUrl) URL.revokeObjectURL(this._frontUrl);
      if (this._backUrl) URL.revokeObjectURL(this._backUrl);
    }

    _cacheDom() {
      this.sizePills = this.querySelector('[data-tb-sizes]');
      this.printBtn = this.querySelector('[data-tb-print-btn]');
      this.printMeta = this.querySelector('[data-tb-print-meta]');
      this.printPriceEl = this.querySelector('[data-tb-print-price]');
      this.uploadEl = this.querySelector('[data-tb-upload]');
      this.printGuideEl = this.querySelector('[data-tb-print-guide]');
      this.fileFrontInput = this.querySelector('[data-tb-file-front]');
      this.fileBackInput = this.querySelector('[data-tb-file-back]');
      this.fileFrontName = this.querySelector('[data-tb-file-front-name]');
      this.fileBackName = this.querySelector('[data-tb-file-back-name]');
      this.printWarning = this.querySelector('[data-tb-print-warning]');
      this.printStepEl = this.querySelector('[data-tb-print-step]');
      this.descEl = this.querySelector('[data-tb-desc]');
      this.basePriceEl = this.querySelector('[data-tb-base-price]');
      this.printRow = this.querySelector('[data-tb-print-row]');
      this.printLine = this.querySelector('[data-tb-print-line]');
      this.printStateEl = this.querySelector('[data-tb-print-state]');
      this.totalEl = this.querySelector('[data-tb-total]');
      this.statusEl = this.querySelector('[data-tb-status]');
    }

    _renderSizePills() {
      if (!this.sizePills) return;
      this.sizePills.innerHTML = '';
      const sizeStep = this.sizePills.closest('.tablebox-craft__step');
      if (this.options.length <= 1) {
        if (sizeStep) sizeStep.hidden = true;
        return;
      }
      if (sizeStep) sizeStep.hidden = false;
      this.options.forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tablebox-craft__pill';
        btn.setAttribute('data-tb-size', opt.id);
        btn.setAttribute('aria-pressed', 'false');
        btn.innerHTML = `<span>${opt.label}</span><span class="tablebox-craft__pill-meta">${opt.sizeLabel || ''}</span>`;
        this.sizePills.appendChild(btn);
      });
    }

    _bind() {
      this.sizePills?.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-tb-size]');
        if (!btn) return;
        this.optionId = btn.getAttribute('data-tb-size');
        this._sync();
      });
      this.printBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const next = !this.wantPrint;
        this.wantPrint = next;
        if (!next) {
          this._setFront(null);
          this._setBack(null);
          this.printSrc?.reset();
          this._sync();
          return;
        }
        this._sync();
        this._openPrintUpload();
      });
      this._bindFileUploads();
    }

    _initPrintSource() {
      const pdp = window.HarfmixCraftPdp;
      this.printSrc =
        pdp && pdp.createPrintSource
          ? pdp.createPrintSource(this, 'tb', {
              onService: () => {
                this._setFront(null);
                this._setBack(null);
              },
              onChange: () => this._sync(),
            })
          : { source: 'upload', isService: false, noteValue: () => '', reset() {}, sync() {} };
    }

    /** Print add-on lives BELOW the buy buttons (same as standbox-craft.js). */
    _placePrintAfterCtas() {
      const step = this.printStepEl || this.printBtn?.closest('.craft-print-step');
      if (!step) return;
      const tryPlace = () => {
        const pdp = window.HarfmixCraftPdp;
        if (pdp && pdp.placePrintAfterCtas) {
          pdp.placePrintAfterCtas(this, step);
          return true;
        }
        return false;
      };
      if (tryPlace()) return;
      [50, 200, 600, 1500].forEach((ms) => setTimeout(tryPlace, ms));
    }



    _bindFileUploads() {
      const bindInput = (input, onFile) => {
        if (!input) return;
        input.addEventListener('change', () => onFile(input.files?.[0] || null));
        const drop = input.closest('label, .tablebox-craft__drop');
        if (!drop) return;
        ['dragenter', 'dragover'].forEach((ev) => {
          drop.addEventListener(ev, (e) => {
            e.preventDefault();
            e.stopPropagation();
            drop.classList.add('is-dragover');
          });
        });
        ['dragleave', 'drop'].forEach((ev) => {
          drop.addEventListener(ev, (e) => {
            e.preventDefault();
            e.stopPropagation();
            drop.classList.remove('is-dragover');
          });
        });
        drop.addEventListener('drop', (e) => {
          const file = e.dataTransfer?.files?.[0];
          if (!file) return;
          try {
            const dt = new DataTransfer();
            dt.items.add(file);
            input.files = dt.files;
          } catch (_) {}
          onFile(file);
        });
      };

      bindInput(this.fileFrontInput, (file) => this._setFront(file));
      bindInput(this.fileBackInput, (file) => this._setBack(file));
    }

    _openPrintUpload({ scroll = true } = {}) {
      this.wantPrint = true;
      if (this.uploadEl) this.uploadEl.hidden = false;
      if (this.printBtn) this.printBtn.setAttribute('aria-pressed', 'true');
      if (scroll) {
        const target = this.uploadEl || this.printStepEl || this.printBtn?.closest('.craft-print-step') || this.printBtn;
        if (target) {
          requestAnimationFrame(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          });
        }
      }
      if (this.printSrc?.isService) {
        this.printSrc.noteEl?.focus?.({ preventScroll: true });
      } else {
        this.fileFrontInput?.focus?.({ preventScroll: true });
      }
    }

    _markHosts() {
      document.querySelectorAll('product-info, .product, .product-information').forEach((el) => {
        el.classList.add('product--tableboxcraft');
        const boot = () => { if (window.HarfmixCraftPdp) { window.HarfmixCraftPdp.init({ quoteOnly: false }); return true; } return false; };
        if (!boot()) [50, 200, 600, 1500].forEach((ms) => setTimeout(boot, ms));
      });
    }

    _interceptCart() {
      const isOurAtcButton = (el) => {
        if (!(el instanceof Element)) return false;
        const btn = el.closest('button[type="submit"], button[name="add"], [name="add"]');
        if (!btn || btn.closest('tablebox-craft')) return false;
        return (
          document.body.contains(this) &&
          !!btn.closest('sticky-add-to-cart, .sticky-add-to-cart, product-form, .product-form, form[action*="/cart/add"]')
        );
      };
      const guard = (event) => {
        if (event.type === 'submit') {
          const form = event.target instanceof HTMLFormElement ? event.target : event.target.closest?.('form');
          if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.action || '')) return;
          if (form.closest('tablebox-craft') || !document.body.contains(this)) return;
          event.preventDefault();
          event.stopPropagation();
          this._addToCart();
          return;
        }
        if (!isOurAtcButton(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        this._addToCart();
      };
      document.addEventListener('click', guard, true);
      document.addEventListener('submit', guard, true);
    }

    _findVariant(contains, excludes) {
      return (
        this.variants.find(
          (v) => includesAny(v.title, contains) && excludesAny(v.title, excludes || [])
        ) || null
      );
    }

    _currentOption() {
      return this.options.find((o) => o.id === this.optionId) || this.options[0] || null;
    }

    _resolved() {
      const opt = this._currentOption();
      const base = opt ? this._findVariant(opt.baseContains, opt.baseExcludes) : null;
      const print = opt ? this._findVariant(opt.printContains, []) : null;
      return { opt, base, print };
    }

    _setFront(file) {
      this.fileFront = file || null;
      if (this._frontUrl) URL.revokeObjectURL(this._frontUrl);
      this._frontUrl = file && file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
      if (this.fileFrontInput && !file) this.fileFrontInput.value = '';
      this._sync();
    }

    _setBack(file) {
      this.fileBack = file || null;
      if (this._backUrl) URL.revokeObjectURL(this._backUrl);
      this._backUrl = file && file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
      if (this.fileBackInput && !file) this.fileBackInput.value = '';
      this._sync();
    }

    _setStatus(msg, tone) {
      if (!this.statusEl) return;
      if (!msg) {
        this.statusEl.hidden = true;
        this.statusEl.textContent = '';
        return;
      }
      this.statusEl.hidden = false;
      this.statusEl.textContent = msg;
      this.statusEl.dataset.tone = tone || 'success';
    }


    _mediaAlt(item) {
      const img = item.querySelector('img.product-media__image, img');
      return norm(img?.getAttribute('alt') || item.getAttribute?.('alt') || '');
    }

    _scoreHay(altRaw, opt) {
      if (!opt) return 0;
      // Match ONLY alt text — CDN URLs/media ids often contain hex fragments like "a4"/"a5".
      const alt = norm(altRaw);
      if (!alt) return 0;
      const excludes = opt.imageExclude || [];
      if (excludes.some((n) => {
        const needle = norm(n);
        return needle && alt.includes(needle);
      })) {
        return 0;
      }
      const matches = opt.imageMatch || [];
      let score = 0;
      matches.forEach((needle, i) => {
        const n = norm(needle);
        if (!n) return;
        if (alt === n || alt.includes(n)) score += Math.max(10, matches.length - i);
      });
      return score;
    }

    _galleryMediaItems(gallery) {
      const gridItems = Array.from(gallery.querySelectorAll('.media-gallery__grid > li, [ref="media[]"]'));
      if (gridItems.length) return gridItems;
      return Array.from(gallery.querySelectorAll('slideshow-slide'));
    }

    _syncProductMedia(opt) {
      const gallery = document.querySelector('.product-information__media media-gallery, media-gallery');
      if (!gallery || !opt) return;

      const items = this._galleryMediaItems(gallery);
      if (!items.length) return;

      const scored = items.map((item, index) => {
        const score = this._scoreHay(this._mediaAlt(item), opt);
        return { item, index, score };
      });

      let firstVisibleIndex = -1;
      let visibleCount = 0;
      scored.forEach(({ item, index, score }) => {
        const match = score > 0;
        if (match) visibleCount += 1;
        item.hidden = !match;
        item.toggleAttribute('hidden', !match);
        item.setAttribute('aria-hidden', match ? 'false' : 'true');
        item.classList.toggle('tb-media-hidden', !match);
        if (match && firstVisibleIndex < 0) firstVisibleIndex = index;
      });

      const showAll = visibleCount === 0;
      if (showAll) {
        scored.forEach(({ item, index }) => {
          item.hidden = false;
          item.toggleAttribute('hidden', false);
          item.setAttribute('aria-hidden', 'false');
          item.classList.remove('tb-media-hidden');
          if (firstVisibleIndex < 0) firstVisibleIndex = index;
        });
      }

      gallery.querySelectorAll('slideshow-slide').forEach((slide) => {
        if (items.includes(slide)) return;
        const match = showAll || this._scoreHay(this._mediaAlt(slide), opt) > 0;
        slide.hidden = !match;
        slide.toggleAttribute('hidden', !match);
        slide.classList.toggle('tb-media-hidden', !match);
      });

      const thumbs = gallery.querySelectorAll(
        'slideshow-controls button[aria-controls], .slideshow-controls__thumbnail, [ref="thumbnails"] button'
      );
      if (thumbs.length === scored.length) {
        thumbs.forEach((thumb, index) => {
          const hide = scored[index]?.item?.hidden;
          thumb.hidden = Boolean(hide);
          thumb.classList.toggle('tb-media-hidden', Boolean(hide));
        });
      }

      const selectTarget = gallery.slideshow || gallery.querySelector('slideshow-component');
      if (selectTarget && typeof selectTarget.select === 'function' && firstVisibleIndex >= 0) {
        try {
          selectTarget.select(firstVisibleIndex, undefined, { animate: false });
        } catch (_) {
          try {
            selectTarget.select(firstVisibleIndex);
          } catch (_) {}
        }
      }
    }

    _sync() {
      const i18n = this.i18n;
      const { opt, base, print } = this._resolved();

      this.sizePills?.querySelectorAll('[data-tb-size]').forEach((btn) => {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-tb-size') === this.optionId ? 'true' : 'false');
      });

      if (this.descEl) this.descEl.innerHTML = opt?.description || '';

      if (this.printBtn) this.printBtn.setAttribute('aria-pressed', this.wantPrint ? 'true' : 'false');
      if (this.printMeta) {
        this.printMeta.textContent = this.wantPrint
          ? this._printBillingMeta(i18n)
          : `${t(i18n, 'printSizeLabel', 'Baskı alanı')}: ${opt?.printArea || ''}`;
      }
      const baseCents = tableboxBaseCents(this.config, opt, base);
      const printCentsAll = tableboxPrintCents(this.config, opt, print);
      const printCents = this.wantPrint ? printCentsAll : 0;
      const printMoney = printCentsAll > 0 || print ? formatMoney(printCentsAll, this.moneyFormat) : '';

      if (this.printPriceEl) {
        this.printPriceEl.textContent = this.wantPrint && printMoney ? `+ ${printMoney}` : '—';
      }
      if (this.uploadEl) this.uploadEl.hidden = !this.wantPrint;
      this.printSrc?.sync(this.wantPrint);
      if (this.printGuideEl) {
        const guideUrl = opt?.printGuideUrl || '';
        if (guideUrl) {
          this.printGuideEl.href = guideUrl;
          this.printGuideEl.hidden = false;
        } else {
          this.printGuideEl.removeAttribute('href');
          this.printGuideEl.hidden = true;
        }
      }

      if (this.printWarning) this.printWarning.hidden = true;

      if (this.fileFrontName) {
        this.fileFrontName.hidden = !this.fileFront;
        this.fileFrontName.textContent = this.fileFront?.name || '';
      }
      if (this.fileBackName) {
        this.fileBackName.hidden = !this.fileBack;
        this.fileBackName.textContent = this.fileBack?.name || '';
      }

      if (this.basePriceEl) this.basePriceEl.textContent = formatMoney(baseCents, this.moneyFormat);
      if (this.printRow) {
        this.printRow.classList.toggle('is-added', this.wantPrint);
        if (this.printStateEl) {
          this.printStateEl.textContent = this.wantPrint
            ? this._printBillingMeta(i18n)
            : t(i18n, 'addonNotAdded', 'Eklenmedi');
        }
        if (this.printLine) {
          this.printLine.textContent = this.wantPrint && printMoney ? `+ ${printMoney}` : '—';
        }
      }
      if (this.totalEl) this.totalEl.textContent = formatMoney(baseCents + printCents, this.moneyFormat);

      if (base?.id) {
        const input = document.querySelector(`form[action*="/cart/add"] [name="id"]`);
        if (input) {
          input.value = String(base.id);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this._syncProductMedia(opt);
      if (!this._mediaBootstrapped) {
        this._mediaBootstrapped = true;
        requestAnimationFrame(() => this._syncProductMedia(this._currentOption()));
        setTimeout(() => this._syncProductMedia(this._currentOption()), 300);
      }
    }


    _hasAnyPrintFile() {
      return Boolean(this.fileFront || this.fileBack);
    }

    _printBillingMeta(i18n) {
      if (this.printSrc?.isService) {
        return t(i18n, 'printSourceServiceValue', 'HarfMix tasarım / baskı hizmeti');
      }
      const both = Boolean(this.fileFront && this.fileBack);
      const one = Boolean((this.fileFront || this.fileBack) && !both);
      if (one) {
        return t(
          i18n,
          'printDualOneSide',
          'Tek yüz yüklendi — yine de 2 adet (çift yüz) ücreti uygulanır'
        );
      }
      return t(i18n, 'printOn', 'Eklendi — 2 adet (çift yüz) ücreti');
    }

    _printSidesSummary(i18n) {
      if (this.fileFront && this.fileBack) {
        return t(i18n, 'printSidesBoth', 'Ön + arka yüz');
      }
      if (this.fileFront) {
        return t(
          i18n,
          'printSidesFrontOnly',
          'Yalnızca ön yüz — 2 adet (çift yüz) ücreti uygulandı'
        );
      }
      if (this.fileBack) {
        return t(
          i18n,
          'printSidesBackOnly',
          'Yalnızca arka yüz — 2 adet (çift yüz) ücreti uygulandı'
        );
      }
      return '';
    }

    async _addToCart() {
      const i18n = this.i18n;
      const { opt, base, print } = this._resolved();
      if (!base?.id || !base.available) {
        this._setStatus(t(i18n, 'variantMissing', 'Varyant bulunamadı.'), 'error');
        return;
      }
      const isService = Boolean(this.printSrc?.isService);
      if (this.wantPrint && !isService && !this._hasAnyPrintFile()) {
        if (this.printWarning) this.printWarning.hidden = false;
        this._openPrintUpload();
        this._setStatus(
          t(i18n, 'printRequired', 'En az bir yüz için baskı dosyası yükleyin veya HarfMix hizmetini seçin.'),
          'error'
        );
        return;
      }
      if (this.wantPrint && !print?.id) {
        this._setStatus(t(i18n, 'variantMissing', 'Baskı varyantı bulunamadı.'), 'error');
        return;
      }

      const fd = new FormData();
      let index = 0;
      const appendItem = (variantId, props, files) => {
        fd.append(`items[${index}][id]`, String(variantId));
        fd.append(`items[${index}][quantity]`, '1');
        Object.entries(props).forEach(([k, v]) => {
          if (v == null || v === '') return;
          fd.append(`items[${index}][properties][${k}]`, String(v));
        });
        (files || []).forEach(({ key, file }) => {
          if (!file) return;
          fd.append(`items[${index}][properties][${key}]`, file, file.name);
        });
        index += 1;
      };

      appendItem(base.id, {
        [t(i18n, 'propType', 'Ürün Tipi')]: this._currentOption()?.headline?.split('—')[0]?.trim() || 'Lightbox',
        [t(i18n, 'propSize', 'Ölçü')]: opt?.label ? `${opt.label} — ${opt.sizeLabel || ''}` : '',
        [t(i18n, 'propPrint', 'Görsel Baskı')]: this.wantPrint ? t(i18n, 'yes', 'Evet') : t(i18n, 'no', 'Hayır'),
      });

      if (this.wantPrint && print?.id) {
        const note = this.printSrc?.noteValue() || '';
        const printProps = {
          [t(i18n, 'propType', 'Ürün Tipi')]: 'Özel Dijital Baskı (2 adet)',
          [t(i18n, 'propSize', 'Ölçü')]: opt?.printArea || '',
          [t(i18n, 'propPrint', 'Görsel Baskı')]: t(i18n, 'yes', 'Evet'),
          [t(i18n, 'propPrintSource', 'Baskı tercihi')]: isService
            ? t(i18n, 'printSourceServiceValue', 'HarfMix tasarım / baskı hizmeti')
            : t(i18n, 'printSourceUploadValue', 'Dosya yükleme'),
          [t(i18n, 'propPrintBilling', 'Baskı ücreti')]: t(
            i18n,
            'printBillingTwo',
            '2 adet — çift yüz (tek yüz yüklense bile)'
          ),
          _tb_addon: 'print',
        };
        if (!isService) {
          printProps[t(i18n, 'propPrintSides', 'Yüklenen yüzler')] = this._printSidesSummary(i18n);
        }
        if (note) {
          printProps[t(i18n, 'propPrintNote', 'Baskı notu')] = note;
        }
        appendItem(
          print.id,
          printProps,
          isService
            ? []
            : [
                { key: t(i18n, 'propPrintFront', 'Ön Yüz Baskı'), file: this.fileFront },
                { key: t(i18n, 'propPrintBack', 'Arka Yüz Baskı'), file: this.fileBack },
              ]
        );
      }

      const sectionIds = cartSectionIds();
      if (sectionIds.length) fd.append('sections', sectionIds.join(','));

      this._setStatus(t(i18n, 'adding', 'Ekleniyor…'), 'success');
      try {
        const res = await fetch(cartAddUrl(), {
          method: 'POST',
          body: fd,
          headers: { Accept: 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || t(i18n, 'addError', 'Sepete eklenemedi.'));
        }
        this._setStatus(t(i18n, 'added', 'Sepete eklendi.'), 'success');
        const lines = [{ merchandiseId: String(base.id), quantity: 1 }];
        if (this.wantPrint && print?.id) lines.push({ merchandiseId: String(print.id), quantity: 1 });
        await notifyHorizonCart(data, lines);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data, bubbles: true }));
      } catch (err) {
        console.error('[TableboxCraft]', err);
        this._setStatus(err.message || t(i18n, 'addError', 'Sepete eklenemedi.'), 'error');
      }
    }
  }

  if (!customElements.get('tablebox-craft')) {
    customElements.define('tablebox-craft', TableboxCraft);
  }
})();
